import { Component, OnDestroy, OnInit } from '@angular/core';
import { ContractService } from '../../contract.service';
import { Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ContractModel, ContractPerimeterModel } from '../../../../../../core/models/contract.model';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DialogConfirmComponent } from '../../../../../../../@vex/components/dialog-confirm/dialog-confirm.component';
import faAngleRight from '@iconify/icons-fa-solid/angle-right';
import faAngleLeft from '@iconify/icons-fa-solid/angle-left';
import { AuthService } from '../../../../../../core/services/auth.service';
import icSearch from '@iconify/icons-ic/twotone-search';
import icClose from '@iconify/icons-ic/twotone-close';
import { GenericEngagementDataPatrimonyEnum } from '../../../../../../core/enums/generic-engagement-data-patrimony.enum';
import { EnergyIdService } from '../../../energy-id/energy-id.service';
import { EquipmentService } from '../../../equipment/equipment.service';
import { BuildingService } from '../../../building/building.service';
import { EstablishmentService } from '../../../establishment/establishment.service';
import { UserProfileService } from '../../../user-profile/user-profile.service';
import { Subscription } from 'rxjs';
import { ContractStatusEnum } from '../../../../../../core/enums/contract-status.enum';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { AffectationErrorHandlerService } from '../../services/affectation-error-handler.service';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';

@Component({
  selector: 'vex-contract-details-perimeter',
  templateUrl: './contract-details-perimeter.component.html',
  styleUrls: ['./contract-details-perimeter.component.scss'],
})
export class ContractDetailsPerimeterComponent implements OnInit, OnDestroy {
  constructor(
    private contractService: ContractService,
    private energyIdService: EnergyIdService,
    private equipmentService: EquipmentService,
    private buildingService: BuildingService,
    private establishmentService: EstablishmentService,
    public authService: AuthService,
    private userProfileService: UserProfileService,
    private router: Router,
    private dialog: MatDialog,
    private affectationErrorHandler: AffectationErrorHandlerService,
    private snackbarService: SnackbarService,
  ) {}
  readonly ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  /** PRED-3230 : sans UPDATE_CONTRACT, le périmètre est consultable mais jamais éditable. */
  isUpdateDisabledPermission = !this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_CONTRACT);
  public faAngleRight = faAngleRight;
  public faAngleLeft = faAngleLeft;
  public icSearch = icSearch;
  public icClose = icClose;
  public selectAllButtonLabel = 'Tout sélectionner';

  public referencePerimeterForm: UntypedFormGroup;
  public itemPerimeterForm: UntypedFormGroup;
  public searchForm: UntypedFormGroup;
  public searchControl: UntypedFormControl = new UntypedFormControl();

  public ContractStatusEnum = ContractStatusEnum;

  establishmentsList: { _id: string; label: string }[];
  itemsList: {
    _id: string;
    label: string;
    genericLabel: string;
    genericId: string;
    establishmentId: string;
    establishmentLabel: string;
    buildingId: string;
    buildingLabel: string;
  }[];
  itemsListNoDuplicate: {
    _id: string;
    label: string;
    genericLabel: string;
    genericId: string;
    establishmentId: string;
    establishmentLabel: string;
    buildingId: string;
    buildingLabel: string;
  }[];

  selectedItems: any[] = [];
  selectedItemsFromChild: ContractPerimeterModel[] = [];

  entity: ContractModel;
  currentDataPatrimony: GenericEngagementDataPatrimonyEnum;
  isReference: boolean;
  messageItemNotAvailable = '';

  isLoading = true;
  loadingState: 'loading' | 'transitioning' | 'loaded' = 'loading';
  private minLoadingTime = 200; // Délai avant de commencer la transition
  private loadingStartTime: number;
  private perimeterInitialized = false; // Flag pour éviter les double appels

  // Nouvelle variable pour gérer le mode édition des contrats validés
  isEditingValidatedContractPerimeter = false;

  // Message d'avertissement pour le mode édition
  temporaryPerimeterWarningMessages: string[] = [
    "Les modifications ne seront appliquées et propagées aux tickets existants qu'après avoir cliqué sur ENREGISTRER",
    "Vous pouvez cliquer sur ANNULER pour revenir à l'état précédent sans appliquer les modifications",
  ];

  subscription = new Subscription();

  protected readonly GenericEngagementDataPatrimonyEnum = GenericEngagementDataPatrimonyEnum;

  ngOnInit() {
    this.initData();
    this.initForm();

    this.searchControl.valueChanges.subscribe(value => {
      this.filterCheckBox(value);
    });

    this.searchForm.valueChanges.subscribe(value => {
      this.filterInputs(value);
    });
  }

  initData() {
    this.isLoading = true;

    // Réinitialiser le flag pour permettre un nouveau chargement
    this.perimeterInitialized = false;

    this.initAffectations();

    const sub = this.contractService.entity$.subscribe((entity: ContractModel) => {
      this.entity = entity;
      this.refreshAvailableItems();

      this.currentDataPatrimony = entity.genericEngagement?.dataPatrimony;
      this.isReference = this.currentDataPatrimony === GenericEngagementDataPatrimonyEnum.REFERENCE;

      // Les affectations peuvent répondre avant le contrat : initAffectations() n'a alors pas pu
      // initialiser la liste (entity absente) et le périmètre restait vide. Rattrapage dès que
      // les deux sont disponibles (PRED-3239).
      if (this.establishmentsList && !this.perimeterInitialized) {
        this.initExistingPerimeter();
      }

      // Détecter automatiquement si on est en mode édition de périmètre temporaire
      // UNIQUEMENT pour les utilisateurs isPredik
      if (entity.temporaryPerimeter && entity.temporaryPerimeter.length > 0 && this.authService.isPredik()) {
        this.isEditingValidatedContractPerimeter = true;
      }

      this.isLoading = false;
    });
    this.subscription.add(sub);
  }

  private initForm() {
    this.itemPerimeterForm = new UntypedFormGroup({
      selectedItem: new UntypedFormControl(),
    });

    this.searchForm = new UntypedFormGroup({
      generics: new UntypedFormControl(''),
      establishments: new UntypedFormControl(''),
      buildings: new UntypedFormControl(''),
    });
  }

  initAffectations() {
    const sub = this.userProfileService.findUserAffectation().subscribe(
      datas => {
        this.establishmentsList = datas.data.map(x => {
          return {
            _id: x.establishment._id,
            label: x.establishment.label,
          };
        });
        this.establishmentsList.sort((a, b) => a.label.localeCompare(b.label));
        if (this.entity) {
          this.initExistingPerimeter();
        }
      },
      error => {
        this.affectationErrorHandler.handle(error, {
          fallbackMessage: "Vous n'avez aucun établissement affecté.",
          width: '500px',
          extraStatusCodes: [400],
          logMessage: 'Erreur lors de la récupération des affectations:',
        });
        this.isLoading = false;
        this.hideSpinnerWithDelay();
      },
    );
    this.subscription.add(sub);
  }

  /**
   * Masque le spinner avec transition fluide et décalée
   * Séquence :
   * 1. Contenu commence fade-in (0-400ms)
   * 2. Spinner reste visible à 100% jusqu'à 250ms
   * 3. Spinner fade-out rapide (250-400ms)
   * 4. État 'loaded' après 400ms total
   */
  private hideSpinnerWithDelay(): void {
    if (!this.loadingStartTime) {
      this.loadingState = 'loaded';
      return;
    }

    const elapsedTime = Date.now() - this.loadingStartTime;
    const remainingTime = Math.max(0, this.minLoadingTime - elapsedTime);

    setTimeout(() => {
      // Passer à l'état transitoire : contenu fade-in, spinner reste visible
      this.loadingState = 'transitioning';

      // Après 400ms total, passer à loaded (spinner aura disparu via animation CSS)
      setTimeout(() => {
        this.loadingState = 'loaded';
      }, 100); // Durée totale : contenu fade-in (400ms) + spinner fade-out (150ms avec delay 250ms)
    }, remainingTime);
  }

  initExistingPerimeter() {
    // Éviter les double appels
    if (this.perimeterInitialized) {
      return;
    }
    this.perimeterInitialized = true;

    // Enregistrer le temps de début du chargement
    if (!this.loadingStartTime) {
      this.loadingStartTime = Date.now();
    }
    this.loadingState = 'loading';

    switch (this.entity.genericEngagement.dataPatrimony) {
      case GenericEngagementDataPatrimonyEnum.REFERENCE:
        // Données synchrones - cacher le spinner avec délai

        this.itemsList = this.establishmentService.transformEstablishmentData(this.establishmentsList);
        this.itemsListNoDuplicate = this.getBaseItemsList();

        this.hideSpinnerWithDelay();
        break;

      case GenericEngagementDataPatrimonyEnum.EQUIPMENT:
        this.equipmentService
          .findPerimetersByEstablishments(
            this.entity,
            this.establishmentsList.map(x => x._id),
          )
          .subscribe(
            result => {
              const filteredResult = result?.filter(item => item != null) || [];

              this.itemsList = this.equipmentService.transformEquipmentData(filteredResult);

              const itemsListNotAvailable = this.itemsList.filter(i => i.buildingId === null).map(i => i.label);
              this.messageItemNotAvailable = itemsListNotAvailable.length > 0 ? 'Pas de bâtiment affecté' : '';
              this.itemsListNoDuplicate = this.getBaseItemsList();

              this.hideSpinnerWithDelay();
            },
            error => {
              this.handlePerimeterError(error);
              this.hideSpinnerWithDelay();
            },
          );
        break;

      case GenericEngagementDataPatrimonyEnum.BUILDING:
        this.buildingService
          .findPerimetersByEstablishments(
            this.entity,
            this.establishmentsList.map(x => x._id),
          )
          .subscribe(
            result => {
              this.itemsList = this.buildingService.transformBuildingData(result);
              this.itemsListNoDuplicate = this.getBaseItemsList();
              this.hideSpinnerWithDelay();
            },
            error => {
              this.handlePerimeterError(error);
              this.hideSpinnerWithDelay();
            },
          );
        break;

      case GenericEngagementDataPatrimonyEnum.ID_ENERGIE:
        this.energyIdService
          .findPerimetersByEstablishments(
            this.entity,
            this.establishmentsList.map(x => x._id),
          )
          .subscribe(
            result => {
              this.itemsList = this.energyIdService.transformEnergyIdData(result);
              this.itemsListNoDuplicate = this.getBaseItemsList();
              this.hideSpinnerWithDelay();
            },
            error => {
              this.handlePerimeterError(error);
              this.hideSpinnerWithDelay();
            },
          );
        break;
    }
  }

  filterCheckBox(value): void {
    // this.initAffectations();
    this.itemsListNoDuplicate = this.getBaseItemsList();

    // if there is something in the inputs search
    if (this.searchForm.value) {
      this.filterInputs(this.searchForm.value, false);
    }

    if (value) {
      const normalizedValue = value.toLowerCase();

      this.itemsListNoDuplicate = this.itemsListNoDuplicate.filter(item => this.matchesSearch(item, normalizedValue));
      this.selectedItems = this.selectedItems.filter(selectedId =>
        this.itemsListNoDuplicate.some(e => e._id === selectedId._id.toString()),
      );
    }

    if (this.selectAllButtonLabel === 'Tout déselectionner') {
      this.selectedItems = this.itemsListNoDuplicate;
    }
  }

  /**
   * La recherche porte sur tous les libellés affichés : élément, générique, établissement et bâtiment.
   * Sur un périmètre Bâtiment/Équipement, chercher un établissement (ex. « ehpad ») ne remontait rien
   * car seul le libellé de l'élément était comparé (PRED-3239).
   */
  private matchesSearch(item: { [key: string]: any }, normalizedValue: string): boolean {
    return [item.label, item.genericLabel, item.establishmentLabel, item.buildingLabel].some((label: string) =>
      label?.toLowerCase().includes(normalizedValue),
    );
  }

  filterInputs(value, resetToBase = true) {
    if (resetToBase) {
      this.itemsListNoDuplicate = this.getBaseItemsList();
    }

    if (value.establishments && value.establishments?._id) {
      this.itemsListNoDuplicate = this.itemsListNoDuplicate.filter(
        list => list.establishmentId === value.establishments.establishmentId,
      );
      this.selectedItems = this.selectedItems.filter(selectedId =>
        this.itemsListNoDuplicate.some(e => e.establishmentId === selectedId.establishmentId),
      );
    }

    if (value.generics && value.generics?._id) {
      this.itemsListNoDuplicate = this.itemsListNoDuplicate.filter(list => list.genericId === value.generics.genericId);
      this.selectedItems = this.selectedItems.filter(selectedId =>
        this.itemsListNoDuplicate.some(e => e.genericId === selectedId.genericId),
      );
    }

    if (value.buildings && value.buildings?._id) {
      this.itemsListNoDuplicate = this.itemsListNoDuplicate.filter(
        list => list.buildingId === value.buildings.buildingId,
      );
      this.selectedItems = this.selectedItems.filter(selectedId =>
        this.itemsListNoDuplicate.some(e => e.buildingId === selectedId.buildingId),
      );
    }

    if (this.selectAllButtonLabel === 'Tout déselectionner') {
      this.selectedItems = this.itemsListNoDuplicate;
    }
  }

  toggleSelection(checked: boolean, itemId) {
    if (checked) {
      this.selectedItems.push(itemId);
    }

    if (!checked) {
      const index = this.selectedItems.findIndex(item => item === itemId);
      if (index !== -1) {
        this.selectedItems.splice(index, 1);
      }
    }
  }

  updateCheckboxSelection(checked: boolean) {
    if (checked) {
      this.selectedItems = this.itemsListNoDuplicate;

      if (!this.isReference) {
        this.selectedItems = this.selectedItems.filter(i => i.buildingId !== null);
      }

      this.selectAllButtonLabel = 'Tout désélectionner';
    }

    if (!checked) {
      this.selectedItems = [];
      this.selectAllButtonLabel = 'Tout sélectionner';
    }
  }

  isSelected(itemId: any): boolean {
    return this.selectedItems.some(selectedItem => selectedItem._id === itemId);
  }

  isNotAvailable(itemId: any): boolean {
    if (!this.isReference) {
      return this.itemsList.some(i => i._id === itemId && i.buildingId === null);
    }
    return false;
  }

  isAllSelected() {
    if (!this.isReference) {
      const filteredItems = this.itemsListNoDuplicate.filter(i => i.buildingId !== null);
      return this.selectedItems.length === filteredItems.length;
    }

    if (this.isReference) {
      return this.selectedItems.length === this.itemsListNoDuplicate.length;
    }
  }

  onSelectedItemsChange(selectedItems: ContractPerimeterModel[]) {
    this.selectedItemsFromChild = selectedItems;
  }

  addItem() {
    // Détecter si on est en mode édition de contrat validé
    let observable;
    if (this.isEditingValidatedContractPerimeter) {
      // Modifier le périmètre temporaire
      observable = this.contractService.addToTemporaryPerimeter(this.entity._id, this.selectedItems);
    } else {
      // Modifier le périmètre normal (mode création)
      observable = this.contractService.addAllPerimeter(this.entity._id, this.selectedItems);
    }

    const sub = observable.subscribe(() => {
      this.reset();
    });
    this.subscription.add(sub);
  }

  deleteItem() {
    // Détecter si on est en mode édition de contrat validé
    let observable;
    if (this.isEditingValidatedContractPerimeter) {
      // Modifier le périmètre temporaire
      observable = this.contractService.removeFromTemporaryPerimeter(this.entity._id, this.selectedItemsFromChild);
    } else {
      // Modifier le périmètre normal (mode création)
      observable = this.contractService.deleteAllPerimeter(this.entity._id, this.selectedItemsFromChild);
    }

    const sub = observable.subscribe(() => {
      this.reset();
    });
    this.subscription.add(sub);
  }

  reset() {
    this.selectedItems = [];
    this.selectedItemsFromChild = [];
    this.selectAllButtonLabel = 'Tout sélectionner';
  }

  clearFilter() {
    this.itemsListNoDuplicate = this.getBaseItemsList();
    this.searchForm.reset({}, { emitEvent: false });
    this.selectAllButtonLabel = 'Tout sélectionner';

    // if there is something in the search bar
    if (this.searchControl.value) {
      this.filterCheckBox(this.searchControl.value);
    }
  }

  removeDuplicatesAndSortByKey(items: any[], key: string): any[] {
    if (!items) {
      return [];
    }

    const uniqueItems = [];
    const addedItems = new Set<string>();

    for (const item of items) {
      if (item[key] != null && !addedItems.has(item[key])) {
        uniqueItems.push(item);
        addedItems.add(item[key]);
      }
    }

    if (key === 'genericId') {
      uniqueItems.sort((a, b) => a.genericLabel.localeCompare(b.genericLabel));
    }

    if (key === 'establishmentId') {
      uniqueItems.sort((a, b) => a.establishmentLabel.localeCompare(b.establishmentLabel));
    }

    if (key === 'buildingId') {
      uniqueItems.sort((a, b) => a.buildingLabel.localeCompare(b.buildingLabel));
    }

    return uniqueItems;
  }

  private getBaseItemsList(): any[] {
    const items = this.removeDuplicatesAndSortByKey(this.itemsList, '_id');
    return this.filterOutDisplayedPerimeter(items);
  }

  private filterOutDisplayedPerimeter(items: any[]): any[] {
    const displayedPerimeter = this.getDisplayedPerimeter();
    if (!displayedPerimeter || displayedPerimeter.length === 0) {
      return items;
    }

    switch (this.entity?.genericEngagement?.dataPatrimony) {
      case GenericEngagementDataPatrimonyEnum.REFERENCE:
        return items.filter(list => !displayedPerimeter.some(perimeter => perimeter.establishmentId === list._id));
      case GenericEngagementDataPatrimonyEnum.EQUIPMENT:
        return items.filter(list => !displayedPerimeter.some(perimeter => perimeter.equipmentId === list._id));
      case GenericEngagementDataPatrimonyEnum.BUILDING:
        return items.filter(list => !displayedPerimeter.some(perimeter => perimeter.buildingId === list._id));
      case GenericEngagementDataPatrimonyEnum.ID_ENERGIE:
        return items.filter(list => !displayedPerimeter.some(perimeter => perimeter.energyIdId === list._id));
      default:
        return items;
    }
  }

  private refreshAvailableItems(): void {
    if (!this.itemsList || !this.entity) {
      return;
    }

    this.itemsListNoDuplicate = this.getBaseItemsList();

    if (this.searchControl?.value) {
      this.filterCheckBox(this.searchControl.value);
      return;
    }

    if (this.searchForm?.value) {
      this.filterInputs(this.searchForm.value, false);
    }
  }

  async switchToUpdateMode() {
    const sub = this.dialog
      .open(DialogConfirmComponent, {
        data: {
          description:
            'Êtes-vous sûr de vouloir modifier le périmètre ? Attention cela peut avoir des conséquences sur les autres étapes.',
          cancelText: 'ANNULER',
          title: 'Modifier le périmètre',
          validText: 'CONFIRMER',
          call$: this.contractService.updateStep(this.entity._id, { creationStep: 2 }),
        },
      })
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.initData();
        }
      });
    this.subscription.add(sub);
  }

  /**
   * Active le mode édition pour les contrats validés (uniquement pour isPredik)
   * Initialise le périmètre temporaire en copiant le périmètre actuel
   */
  enableValidatedContractPerimeterEdit() {
    const sub = this.contractService.initializeTemporaryPerimeter(this.entity._id).subscribe(
      response => {
        // Le périmètre temporaire a été initialisé
        // Activer le mode édition
        this.isEditingValidatedContractPerimeter = true;
        this.perimeterInitialized = false;
        this.initExistingPerimeter();
      },
      error => {
        console.error("Erreur lors de l'initialisation du périmètre temporaire:", error);
        // TODO: Ajouter une notification d'erreur
      },
    );
    this.subscription.add(sub);
  }

  /**
   * Annule les modifications du périmètre pour les contrats validés
   * Nettoie le périmètre temporaire et sort du mode édition
   */
  cancelValidatedContractPerimeterEdit() {
    const sub = this.contractService.cancelTemporaryPerimeter(this.entity._id).subscribe(
      response => {
        this.isEditingValidatedContractPerimeter = false;
        this.reset();
        this.perimeterInitialized = false;
        this.initExistingPerimeter();
        this.snackbarService.success('Modifications annulées.');
      },
      error => {
        console.error("Erreur lors de l'annulation du périmètre temporaire:", error);
        this.snackbarService.danger("Une erreur est survenue lors de l'annulation. Veuillez réessayer.");
      },
    );
    this.subscription.add(sub);
  }

  /**
   * Enregistre les modifications du périmètre pour les contrats validés
   * Applique le périmètre temporaire au périmètre officiel et met à jour les tickets
   */
  saveValidatedContractPerimeterEdit() {
    const sub = this.contractService.applyTemporaryPerimeter(this.entity._id).subscribe(
      response => {
        this.isEditingValidatedContractPerimeter = false;
        this.reset();
        this.perimeterInitialized = false;
        this.initExistingPerimeter();
        this.snackbarService.success('Le périmètre a été enregistré avec succès.');
      },
      error => {
        console.error("Erreur lors de l'application du périmètre:", error);
        this.snackbarService.danger("Une erreur est survenue lors de l'enregistrement. Veuillez réessayer.");
      },
    );
    this.subscription.add(sub);
  }

  /**
   * Vérifie si le bouton de modification du périmètre doit être affiché
   * Uniquement pour les utilisateurs isPredik et les contrats validés non archivés/expirés
   */
  canEditValidatedContractPerimeter(): boolean {
    if (!this.entity) {
      return false;
    }

    // Vérifier si l'utilisateur est isPredik
    const isPredik = this.authService.isPredik();

    // Vérifier le statut du contrat
    const isValidated = this.entity.status === ContractStatusEnum.VALIDATED;
    const isNotArchived = !this.entity.isArchive;
    const isNotExpired = this.entity.status !== ContractStatusEnum.EXPIRED;

    return isPredik && isValidated && isNotArchived && isNotExpired;
  }

  /**
   * Retourne le périmètre à afficher (temporaire s'il existe, sinon officiel)
   */
  getDisplayedPerimeter(): ContractPerimeterModel[] {
    // En mode édition d'un contrat validé, on affiche toujours le temporaire,
    // même vide (sinon retirer le dernier élément ferait réapparaître l'officiel).
    if (this.isEditingValidatedContractPerimeter) {
      return this.entity.temporaryPerimeter || [];
    }
    if (this.entity.temporaryPerimeter && this.entity.temporaryPerimeter.length > 0) {
      return this.entity.temporaryPerimeter;
    }
    return this.entity.perimeter || [];
  }

  updateStep(step: number) {
    const sub = this.contractService.updateStep(this.entity._id, { creationStep: step }).subscribe(res => {
      if (step === 3) {
        this.router.navigate(['/management/engagement/', this.entity._id, 'cga']).then();
      }
    });
    this.subscription.add(sub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  /**
   * Gère les erreurs liées aux affectations lors du chargement du périmètre
   */
  private handlePerimeterError(error: any): void {
    this.affectationErrorHandler.handle(error, {
      logMessage: 'Erreur lors du chargement du périmètre:',
    });
  }
}
